import React, { useId, useState } from 'react';
import { PiTrashLight, PiUploadSimpleLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { FileButton } from 'src/modules/shared/components/atoms/buttons/file-button/file-button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import {
  checkWebsiteLogo,
  isVectorLogoUrl,
  type WebsiteLogoSize,
} from '../../utils/website-logo-guidance.util';
import { WEBSITE_LOGO_ACCEPT } from '../../utils/website-logo-upload.util';
import { WebsiteLogoFeedback } from './website-logo-feedback';
import { WebsiteLogoFindings } from './website-logo-findings';
import { WebsiteLogoPreview } from './website-logo-preview';
import { WebsiteLogoSpecs } from './website-logo-specs';

/** Outer box and label classes for a framed picker or one embedded in a card. */
const LOGO_INPUT_LAYOUTS: Record<
  'framed' | 'embedded',
  { container: string; labelGroup?: string }
> = {
  framed: {
    container: 'space-y-4 rounded-lg border border-neutral-200 p-4',
  },
  embedded: { container: 'space-y-4', labelGroup: 'sr-only' },
};

/** Element ids of the logo picker and its messages. */
interface LogoInputIds {
  /** Hidden file input. */
  input: string;
  /** Format and size help. */
  help: string;
  /** Inactive-design notice. */
  inactive: string;
  /** Upload error. */
  error: string;
  /** Deferred cleanup notice. */
  deferredCleanup: string;
  /** Ids describing the file input, for the messages currently shown. */
  describedBy: string;
}

/**
 * Builds the picker's element ids and the `aria-describedby` list of the
 * messages that are currently shown.
 *
 * @param baseId - Unique id from `useId`.
 * @param shown - Which optional messages are visible.
 * @returns The element ids.
 */
const buildLogoInputIds = (
  baseId: string,
  shown: { inactive: boolean; error: boolean; deferredCleanup: boolean }
): LogoInputIds => {
  const input = `website-logo-file-${baseId}`;
  const ids = {
    input,
    help: `${input}-help`,
    inactive: `${input}-inactive`,
    error: `${input}-error`,
    deferredCleanup: `${input}-deferred-cleanup`,
  };
  const describedBy = [
    ids.help,
    shown.inactive ? ids.inactive : undefined,
    shown.error ? ids.error : undefined,
    shown.deferredCleanup ? ids.deferredCleanup : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return { ...ids, describedBy };
};

/** Natural size of a logo, tied to the URL it was measured from. */
interface MeasuredLogo extends WebsiteLogoSize {
  /** Logo URL the size belongs to. */
  url: string;
}

/** Props for the managed website-logo control. */
interface WebsiteLogoInputProps {
  /** Accessible label for the file input. */
  label: string;
  /** Supported-format and size guidance. */
  helpText: string;
  /** Explanation shown when the selected template does not support logos. */
  inactiveText: string;
  /** Accessible name and tooltip of the remove action, such as "Quitar logo". */
  removeLabel: string;
  /** Notice explaining deferred cleanup of a currently published logo. */
  deferredCleanupText: string;
  /** Accessible alternative text for the saved-logo preview. */
  previewAlt: string;
  /** Accessible label for upload progress. */
  progressLabel: string;
  /** Stable URL of the currently selected logo. */
  logoUrl?: string;
  /** Whether the current user may modify website settings. */
  disabled: boolean;
  /** Whether logo support is inactive for the selected template. */
  inactive: boolean;
  /** Whether an upload, finalization, or save operation is running. */
  busy: boolean;
  /** Current upload progress from zero through one hundred. */
  progress: number;
  /** Polite status announcement for the current operation. */
  statusMessage?: string;
  /** Actionable validation or upload error announcement. */
  errorMessage?: string;
  /** Whether published storage cleanup is deferred until publication. */
  showDeferredCleanup: boolean;
  /** Called after the user selects a file. */
  onFileSelected: (file: File) => void;
  /** Called when the selected logo should be removed from the draft. */
  onRemove: () => void;
  /** Extra classes for the outer box, merged over the defaults. */
  className?: string;
  /** Drops the frame and keeps the label and help text for screen readers only, for use inside a card that shows them. */
  embedded?: boolean;
}

/**
 * Renders the logo picker for website settings: a preview tile with the
 * change and remove actions, a check of how the uploaded logo will look in
 * the designs, and the recommended logo specs.
 *
 * The parent owns logo validation, upload, and draft removal state. This
 * component forwards the selected file, keeps the current preview visible
 * while logo support is inactive, disables actions while unavailable or
 * busy, and announces progress, status, and failures accessibly.
 *
 * @param props - Labels, state, and callbacks that control the logo picker.
 * @returns The preview, actions, logo check, specs and operation feedback.
 */
export const WebsiteLogoInput: React.FC<WebsiteLogoInputProps> = ({
  label,
  helpText,
  inactiveText,
  removeLabel,
  deferredCleanupText,
  previewAlt,
  progressLabel,
  logoUrl,
  disabled,
  inactive,
  busy,
  progress,
  statusMessage,
  errorMessage,
  showDeferredCleanup,
  onFileSelected,
  onRemove,
  className,
  embedded = false,
}) => {
  const { t } = useI18n();
  const [measured, setMeasured] = useState<MeasuredLogo | null>(null);
  const layout = LOGO_INPUT_LAYOUTS[embedded ? 'embedded' : 'framed'];
  const ids = buildLogoInputIds(useId(), {
    inactive,
    error: Boolean(errorMessage),
    deferredCleanup: showDeferredCleanup,
  });
  // Tied to the URL so a replaced logo never shows the previous size.
  const size = logoUrl && measured?.url === logoUrl ? measured : null;

  return (
    <div className={classes(layout.container, className)}>
      <div className={layout.labelGroup}>
        <label
          htmlFor={ids.input}
          className="text-sm font-medium text-neutral-900"
        >
          {label}
        </label>
        <Text id={ids.help} size="tiny" color="secondary" className="mt-1">
          {helpText}
        </Text>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <WebsiteLogoPreview
          logoUrl={logoUrl}
          alt={previewAlt}
          emptyText={t('websites.settings.logo.empty')}
          onMeasure={(next) => {
            if (logoUrl) setMeasured({ url: logoUrl, ...next });
          }}
        />
        <div className="min-w-0 space-y-2">
          {size ? (
            <Text size="small" color="secondary">
              {t('websites.settings.logo.dimensions', {
                width: size.width,
                height: size.height,
              })}
            </Text>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <FileButton
              inputId={ids.input}
              accept={WEBSITE_LOGO_ACCEPT}
              describedBy={ids.describedBy}
              disabled={disabled || inactive || busy}
              loading={busy}
              ariaLabel={
                logoUrl ? t('websites.settings.logo.changeLabel') : undefined
              }
              onSelect={onFileSelected}
            >
              <PiUploadSimpleLight aria-hidden className="h-4 w-4" />
              {t(
                logoUrl
                  ? 'websites.settings.logo.change'
                  : 'websites.settings.logo.upload'
              )}
            </FileButton>
            {logoUrl ? (
              <Tooltip content={removeLabel} placement="top" size="sm">
                <Button
                  type="button"
                  isIconOnly
                  size="sm"
                  variant="danger"
                  appearance="ghost"
                  onClick={onRemove}
                  disabled={disabled || busy}
                  aria-label={removeLabel}
                  aria-describedby={
                    showDeferredCleanup ? ids.deferredCleanup : undefined
                  }
                >
                  <PiTrashLight aria-hidden className="h-4 w-4" />
                </Button>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>

      {size ? (
        <WebsiteLogoFindings
          size={size}
          findings={checkWebsiteLogo(size, isVectorLogoUrl(size.url))}
        />
      ) : null}
      <WebsiteLogoFeedback
        inactive={
          inactive ? { id: ids.inactive, text: inactiveText } : undefined
        }
        progress={busy ? { value: progress, label: progressLabel } : undefined}
        statusMessage={statusMessage}
        deferredCleanup={
          showDeferredCleanup
            ? { id: ids.deferredCleanup, text: deferredCleanupText }
            : undefined
        }
        error={errorMessage ? { id: ids.error, text: errorMessage } : undefined}
      />

      <WebsiteLogoSpecs />
    </div>
  );
};
